const getVariantSource = (product) => {
  if (!product) return null;

  if (product.variantMatrix?.variants || product.variantMatrix?.attributes) {
    return product.variantMatrix;
  }

  if (product.variations && typeof product.variations === 'object' && !Array.isArray(product.variations)) {
    return product.variations;
  }

  return null;
};

const normalizeSelection = (selection = {}) => {
  return Object.keys(selection)
    .filter((key) => selection[key] !== undefined && selection[key] !== null && selection[key] !== '')
    .reduce((acc, key) => {
      acc[key] = selection[key];
      return acc;
    }, {});
};

export const hasStorefrontVariants = (product) => {
  if (!product) return false;

  const source = getVariantSource(product);
  if (source?.attributes?.length > 0 && source?.variants?.length > 0) {
    return true;
  }

  return Array.isArray(product.variations) && product.variations.some((variation) => variation?.values?.length > 0);
};

export const getStorefrontVariantAttributes = (product) => {
  const source = getVariantSource(product);
  if (source?.attributes?.length > 0) {
    return source.attributes;
  }

  if (Array.isArray(product?.variations)) {
    return product.variations.map((variation) => ({
      name: variation.name || variation.type,
      values: variation.values?.map((value) => {
        if (typeof value === 'string') return value;
        return value?.value ?? value?.name ?? '';
      }).filter(Boolean) || []
    }));
  }

  return [];
};

export const getStorefrontVariants = (product) => {
  const source = getVariantSource(product);
  if (source?.variants?.length > 0) {
    return source.variants;
  }

  return [];
};

export const getStorefrontVariantPriceRange = (product) => {
  const variants = getStorefrontVariants(product);
  const prices = variants
    .filter((variant) => variant?.price !== undefined && variant?.price !== null)
    .map((variant) => parseFloat(variant.price))
    .filter((price) => !Number.isNaN(price));

  if (prices.length > 0) {
    return {
      min: Math.min(...prices),
      max: Math.max(...prices)
    };
  }

  if (Array.isArray(product?.variations)) {
    const legacyPrices = [];
    product.variations.forEach((variation) => {
      variation.values?.forEach((value) => {
        const price = typeof value === 'object' ? parseFloat(value.price) : NaN;
        if (!Number.isNaN(price)) legacyPrices.push(price);
      });
    });
    if (legacyPrices.length > 0) {
      return {
        min: Math.min(...legacyPrices),
        max: Math.max(...legacyPrices)
      };
    }
  }

  return null;
};

export const findStorefrontVariantBySelection = (product, selection = {}) => {
  const normalizedSelection = normalizeSelection(selection);
  const variants = getStorefrontVariants(product);
  if (!variants.length || Object.keys(normalizedSelection).length === 0) return null;

  return variants.find((variant) => {
    const combination = variant?.combination || variant?.attributes || {};
    const combinationEntries = Object.entries(combination);
    const selectionEntries = Object.entries(normalizedSelection);

    if (combinationEntries.length !== selectionEntries.length) {
      return false;
    }

    return selectionEntries.every(([key, value]) => {
      return combination[key] === value || variant?.[key] === value;
    });
  }) || null;
};

export const getStorefrontVariantAvailability = (product) => {
  const variants = getStorefrontVariants(product);
  if (variants.length > 0) {
    const enabledVariants = variants.filter((variant) => variant?.enabled !== false);
    const quantities = enabledVariants.map((variant) => {
      const value = variant?.quantity ?? variant?.stock ?? 0;
      return Number.isFinite(Number(value)) ? Number(value) : 0;
    });
    const totalQuantity = quantities.reduce((sum, value) => sum + value, 0);
    const maxQuantity = quantities.length > 0 ? Math.max(...quantities) : 0;
    const lowStockCount = quantities.filter((value) => value > 0 && value <= 5).length;

    return {
      hasStock: maxQuantity > 0,
      availableQuantity: totalQuantity,
      lowStockCount
    };
  }

  const quantity = Number(product?.stock ?? product?.quantity ?? 0);
  return {
    hasStock: quantity > 0,
    availableQuantity: quantity,
    lowStockCount: quantity > 0 && quantity <= 5 ? 1 : 0
  };
};

export const getStorefrontSelectedVariantAvailability = (product, selection = {}) => {
  const matchedVariant = findStorefrontVariantBySelection(product, selection);
  if (!matchedVariant) {
    return null;
  }

  const quantity = Number(matchedVariant?.quantity ?? matchedVariant?.stock ?? 0);
  return {
    variant: matchedVariant,
    hasStock: quantity > 0,
    availableQuantity: quantity
  };
};

export const findStorefrontVariantByIdentity = (product, identity = {}) => {
  const variants = getStorefrontVariants(product);
  if (!variants.length) return null;

  const variantId = identity?.variantId || identity?.id || null;
  if (variantId) {
    const matchedById = variants.find((variant) => {
      const candidateId = variant?.id || variant?._id || null;
      return candidateId && String(candidateId) === String(variantId);
    });
    if (matchedById) return matchedById;
  }

  const selection = identity?.variantCombination || identity?.variations || identity?.selection || {};
  if (selection && typeof selection === 'object') {
    return findStorefrontVariantBySelection(product, selection);
  }

  return null;
};

export const getStorefrontLineItemAvailability = (product, item = {}) => {
  const matchedVariant = findStorefrontVariantByIdentity(product, {
    variantId: item.variantId || item.product?.variantId || null,
    variantCombination: item.variantCombination || item.variations || null
  });

  if (matchedVariant) {
    const quantity = Number(matchedVariant?.quantity ?? matchedVariant?.stock ?? 0);
    return {
      variant: matchedVariant,
      hasStock: quantity > 0,
      availableQuantity: quantity
    };
  }

  return getStorefrontVariantAvailability(product);
};

export const getStorefrontVariantIdentity = (variant) => {
  if (!variant) return null;

  return {
    variantId: variant.id || variant._id || null,
    variantSku: variant.sku || variant.variantSku || null,
    variantName: variant.name || variant.label || null,
    variantCombination: variant.combination || variant.attributes || null
  };
};
